/**
 * Session tenant context.
 *
 * Every Firestore/Storage path is tenant-scoped ({ns}/{tenantId}/...).
 * The tenant is resolved ONCE per session — from the signed-in user's
 * `tenantId` custom claim (office apps / team) or from the URL (public
 * supplier pages build paths explicitly and never call setTenantId).
 *
 * The tenant never changes mid-session: sign-out performs a full page
 * reload, which is load-bearing — many hooks memoize their queries with
 * useMemo(..., []) and would otherwise keep streaming the old tenant.
 */

export const TENANT_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,30}$/;

let currentTenantId: string | null = null;

/** Set the session tenant. Called once after login, before any Firestore query mounts. */
export function setTenantId(tenantId: string): void {
  if (!TENANT_ID_PATTERN.test(tenantId)) {
    throw new Error(`Invalid tenantId: "${tenantId}"`);
  }
  if (currentTenantId !== null && currentTenantId !== tenantId) {
    throw new Error(
      `Tenant already set ("${currentTenantId}") — sign out (full reload) to switch tenants`
    );
  }
  currentTenantId = tenantId;
}

export function getTenantId(): string | null {
  return currentTenantId;
}

/**
 * The tenant segment used by COLLECTIONS / storagePath.
 *
 * Throws if no tenant has been resolved yet — every session must call
 * setTenantId() (from the signed-in user's `tenantId` custom claim) before
 * any Firestore/Storage path is built.
 */
export function requireTenantId(): string {
  if (currentTenantId === null) {
    throw new Error('No tenant resolved for this session');
  }
  return currentTenantId;
}

/** Tests + sign-out only. */
export function clearTenantId(): void {
  currentTenantId = null;
}
